// Customer.js - UPDATED ADMIN WITH PLAN DISPLAY

import { useEffect, useState } from "react";
import { 
  Container, Table, Spinner, Button, Modal, Form, Alert, 
  Tabs, Tab, Badge, Dropdown, Card, Row, Col 
} from "react-bootstrap";
import { motion } from "framer-motion";
import { 
  FaUserPlus, FaEdit, FaTrash, FaUsers, FaStore, 
  FaCrown, FaRocket, FaChartLine, FaEye, FaEyeSlash,
  FaCheckCircle, FaTimesCircle, FaClock, FaBan,
  FaPlay, FaPause, FaExclamationTriangle, FaHistory,
  FaTag, FaRupeeSign
} from "react-icons/fa";
import Header from "../../component/header/header";
import Sidebar from "../../component/sidebar/sidebar";
import "./customer.css";

// ✅ IMPORT PLAN CONFIG
import { PLAN_DETAILS, PLAN_OPTIONS, getPlanDetails } from "../../config/planConfig";

const API_BASE = process.env.REACT_APP_API_URL + "/customers";

// Status options with colors and icons
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'success', icon: <FaCheckCircle /> },
  { value: 'pending', label: 'Pending', color: 'warning', icon: <FaClock /> },
  { value: 'inactive', label: 'Inactive', color: 'secondary', icon: <FaPause /> },
  { value: 'suspended', label: 'Suspended', color: 'danger', icon: <FaBan /> }
];

const Customer = () => {
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("customers");
  const [authError, setAuthError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Modal states
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorForm, setVendorForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    company: "",
    plan: "STARTER",
    status: "active"
  });
  const [modalLoading, setModalLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showPassword, setShowPassword] = useState(false);

  // Status management states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [statusActionLoading, setStatusActionLoading] = useState(false);
  
  // Bulk action states
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  const [bulkAction, setBulkAction] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  
  // Stats states
  const [vendorStats, setVendorStats] = useState({
    statusCounts: { total: 0, active: 0, inactive: 0, pending: 0, suspended: 0 },
    recentChanges: []
  });
  const [showStats, setShowStats] = useState(false);

  // Get token and user role
  const getToken = () => localStorage.getItem("token");
  const getUserRole = () => {
    try {
      const token = getToken();
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.role ? payload.role.toLowerCase() : null;
    } catch (err) {
      console.error("Error parsing token:", err);
      return null;
    }
  };

  // Fetch Customers
  const fetchCustomers = async () => {
    try {
      const token = getToken();
      if (!token) {
        setAuthError("No authentication token found.");
        return;
      }

      const res = await fetch(`${API_BASE}/`, {
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });

      if (res.status === 401 || res.status === 403) {
        setAuthError("Admin access required.");
        return;
      }

      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
      setAuthError("");
    } catch (err) {
      console.error("Fetch customers error:", err);
      setAuthError("Failed to fetch customers.");
    }
  };

  // Fetch Vendors
  const fetchVendors = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const res = await fetch(`${API_BASE}/vendors`, {
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });

      if (res.status === 403) return;

      const data = await res.json();
      setVendors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch vendors error:", err);
    }
  };

  // Fetch vendor statistics
  const fetchVendorStats = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const res = await fetch(`${API_BASE}/vendors/stats`, {
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });

      if (res.ok) {
        const data = await res.json();
        setVendorStats(data);
      }
    } catch (err) {
      console.error("Fetch stats error:", err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const userRole = getUserRole();
      
      if (userRole !== "admin") {
        setAuthError("Admin access required.");
        setLoading(false);
        return;
      }
      
      await Promise.all([fetchCustomers(), fetchVendors(), fetchVendorStats()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Handle Vendor Form Input
  const handleVendorInput = (e) => {
    setVendorForm({
      ...vendorForm,
      [e.target.name]: e.target.value
    });
  };

  // Open Add Vendor Modal
  const handleAddVendor = () => {
    setEditingVendor(null);
    setVendorForm({
      name: "",
      email: "",
      password: "",
      phone: "",
      company: "",
      plan: "STARTER",
      status: "active"
    });
    setShowVendorModal(true);
  };

  // Open Edit Vendor Modal
  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setVendorForm({
      name: vendor.name || "",
      email: vendor.email || "",
      password: "",
      phone: vendor.phone || "",
      company: vendor.company || "",
      plan: vendor.plan || "STARTER",
      status: vendor.status || "active"
    });
    setShowVendorModal(true);
  };

  // Open Status Change Modal
  const handleStatusChange = (vendor, status) => {
    setSelectedVendor(vendor);
    setNewStatus(status);
    setStatusReason("");
    setShowStatusModal(true);
  };

  // Get plan details
  const getPlanDetailsForVendor = (planValue) => {
    return PLAN_DETAILS[planValue] || PLAN_DETAILS['STARTER'];
  };

  // Get plan badge color
  const getPlanBadgeColor = (plan) => {
    const colors = {
      'STARTER': 'secondary',
      'GROWTH': 'primary',
      'PREMIUM': 'warning'
    };
    return colors[plan] || 'secondary';
  };

  // Get status color
  const getStatusBadgeColor = (status) => {
    const colors = {
      'active': 'success',
      'pending': 'warning',
      'inactive': 'secondary',
      'suspended': 'danger'
    };
    return colors[status] || 'secondary';
  };

  // Save Vendor
  const handleSaveVendor = async () => {
    if (!vendorForm.name || !vendorForm.email) {
      setMessage({ type: "error", text: "Name and email are required" });
      return;
    }

    if (!editingVendor && !vendorForm.password) {
      setMessage({ type: "error", text: "Password is required" });
      return;
    }

    setModalLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const token = getToken();
      const url = editingVendor 
        ? `${API_BASE}/vendors/${editingVendor._id}`
        : `${API_BASE}/vendors`;
      
      const method = editingVendor ? "PUT" : "POST";
      
      const planDetails = getPlanDetailsForVendor(vendorForm.plan);
      
      const data = { 
        name: vendorForm.name,
        email: vendorForm.email,
        phone: vendorForm.phone,
        company: vendorForm.company,
        plan: vendorForm.plan,
        planName: planDetails.name,
        commissionRate: planDetails.commissionRate,
        status: vendorForm.status
      };
      
      if (vendorForm.password) {
        data.password = vendorForm.password;
      }

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      const result = await res.json();

      if (res.ok) {
        setMessage({ type: "success", text: result.message || "Vendor saved successfully" });
        await Promise.all([fetchVendors(), fetchVendorStats()]);
        setTimeout(() => {
          setShowVendorModal(false);
          setMessage({ type: "", text: "" });
        }, 1500);
      } else {
        setMessage({ type: "error", text: result.message || "Failed to save vendor" });
      }
    } catch (err) {
      console.error("Save vendor error:", err);
      setMessage({ type: "error", text: "Server error. Please try again." });
    } finally {
      setModalLoading(false);
    }
  };

  // Delete Vendor
  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;

    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/vendors/${vendorId}`, {
        method: "DELETE",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const result = await res.json();

      if (res.ok) {
        setMessage({ type: "success", text: result.message || "Vendor deleted successfully" });
        await Promise.all([fetchVendors(), fetchVendorStats()]);
        setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      } else {
        setMessage({ type: "error", text: result.message || "Failed to delete vendor" });
      }
    } catch (err) {
      console.error("Delete vendor error:", err);
      setMessage({ type: "error", text: "Failed to delete vendor" });
    }
  };

  // Update vendor status
  const handleUpdateStatus = async () => {
    if (!selectedVendor || !newStatus) {
      setMessage({ type: "error", text: "Invalid status update" });
      return;
    }

    setStatusActionLoading(true);
    
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/vendors/${selectedVendor._id}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          status: newStatus,
          reason: statusReason
        })
      });

      const result = await res.json();

      if (res.ok) {
        const statusLabel = STATUS_OPTIONS.find(s => s.value === newStatus)?.label || newStatus;
        setMessage({ 
          type: "success", 
          text: `${selectedVendor.name}'s status updated to ${statusLabel}` 
        });
        await Promise.all([fetchVendors(), fetchVendorStats()]);
        setShowStatusModal(false);
        setSelectedVendor(null);
        setNewStatus("");
        setStatusReason("");
      } else {
        setMessage({ type: "error", text: result.message || "Failed to update status" });
      }
    } catch (err) {
      console.error("Status update error:", err);
      setMessage({ type: "error", text: "Failed to update status" });
    } finally {
      setStatusActionLoading(false);
    }
  };

  // Bulk action handler
  const handleBulkAction = async () => {
    if (selectedVendors.length === 0 || !bulkAction) {
      setMessage({ type: "error", text: "Select vendors and action" });
      return;
    }

    setStatusActionLoading(true);
    
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/vendors/bulk-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          vendorIds: selectedVendors,
          status: bulkAction,
          reason: bulkReason
        })
      });

      const result = await res.json();

      if (res.ok) {
        const statusLabel = STATUS_OPTIONS.find(s => s.value === bulkAction)?.label || bulkAction;
        setMessage({ type: "success", text: `${result.modifiedCount} vendors updated to ${statusLabel}` });
        await Promise.all([fetchVendors(), fetchVendorStats()]);
        setSelectedVendors([]);
        setShowBulkActionModal(false);
        setBulkAction("");
        setBulkReason("");
      } else {
        setMessage({ type: "error", text: result.message || "Bulk action failed" });
      }
    } catch (err) {
      console.error("Bulk action error:", err);
      setMessage({ type: "error", text: "Bulk action failed" });
    } finally {
      setStatusActionLoading(false);
    }
  };

  // Toggle vendor selection for bulk actions
  const toggleVendorSelection = (vendorId) => {
    setSelectedVendors(prev => {
      if (prev.includes(vendorId)) {
        return prev.filter(id => id !== vendorId);
      } else {
        return [...prev, vendorId];
      }
    });
  };

  // Select all vendors
  const selectAllVendors = () => {
    if (selectedVendors.length === filteredVendors.length) {
      setSelectedVendors([]);
    } else {
      setSelectedVendors(filteredVendors.map(v => v._id));
    }
  };

  // Filter vendors
  const filteredVendors = vendors.filter(vendor => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = vendor.name?.toLowerCase().includes(search) ||
                         vendor.email?.toLowerCase().includes(search) ||
                         vendor.company?.toLowerCase().includes(search);
    
    const matchesStatus = statusFilter === "all" || vendor.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Filter customers
  const filteredCustomers = customers.filter(customer => {
    const search = searchTerm.toLowerCase();
    return customer.name?.toLowerCase().includes(search) ||
           customer.email?.toLowerCase().includes(search) ||
           customer.phone?.includes(search);
  });

  // Get status change options for a vendor
  const getStatusChangeOptions = (vendor) => {
    const options = [];
    const currentStatus = vendor.status;
    
    STATUS_OPTIONS.forEach(opt => {
      if (opt.value !== currentStatus) {
        options.push(opt);
      }
    });
    
    return options;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="admin-layout">
          <Sidebar />
          <main className="admin-content">
            <Container>
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading...</p>
              </div>
            </Container>
          </main>
        </div>
      </>
    );
  }

  if (authError) {
    return (
      <>
        <Header />
        <div className="admin-layout">
          <Sidebar />
          <main className="admin-content">
            <Container>
              <Alert variant="danger" className="mt-4">
                <h5>Access Denied</h5>
                <p>{authError}</p>
                <Button variant="primary" onClick={() => window.location.href = "/login"}>
                  Go to Login
                </Button>
              </Alert>
            </Container>
          </main>
        </div>
      </>
    );
  }

  return (
    <div>
      <Header />
      <div className="admin-layout">
        <Sidebar />
        <main className="admin-content">
          <Container fluid>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="d-flex justify-content-between align-items-center mb-4"
            >
              <h4>Customer & Vendor Management</h4>
              <div className="d-flex gap-2">
                {activeTab === "vendors" && (
                  <Form.Select
                    style={{ width: '150px' }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                )}
                
                <Form.Control
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: '250px' }}
                />
                
                {activeTab === "vendors" && (
                  <>
                    {selectedVendors.length > 0 && (
                      <Button 
                        variant="warning" 
                        onClick={() => setShowBulkActionModal(true)}
                      >
                        Bulk Action ({selectedVendors.length})
                      </Button>
                    )}
                    
                    <Button 
                      variant="info" 
                      onClick={() => setShowStats(!showStats)}
                    >
                      <FaHistory className="me-1" /> Stats
                    </Button>
                    
                    <Button variant="primary" onClick={handleAddVendor}>
                      <FaUserPlus className="me-2" /> Add Vendor
                    </Button>
                  </>
                )}
              </div>
            </motion.div>

            {/* Stats Cards */}
            {showStats && activeTab === "vendors" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mb-4"
              >
                <Row>
                  <Col md={3}>
                    <Card className="text-center">
                      <Card.Body>
                        <h6 className="text-muted">Total Vendors</h6>
                        <h3>{vendorStats.statusCounts.total}</h3>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center border-success">
                      <Card.Body>
                        <h6 className="text-muted">Active</h6>
                        <h3 className="text-success">{vendorStats.statusCounts.active}</h3>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center border-warning">
                      <Card.Body>
                        <h6 className="text-muted">Pending</h6>
                        <h3 className="text-warning">{vendorStats.statusCounts.pending}</h3>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center border-danger">
                      <Card.Body>
                        <h6 className="text-muted">Suspended</h6>
                        <h3 className="text-danger">{vendorStats.statusCounts.suspended}</h3>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                
                {vendorStats.recentChanges.length > 0 && (
                  <Card className="mt-3">
                    <Card.Header>
                      <FaHistory className="me-2" /> Recent Status Changes
                    </Card.Header>
                    <Card.Body>
                      <Table size="sm">
                        <thead>
                          <tr>
                            <th>Vendor</th>
                            <th>From</th>
                            <th>To</th>
                            <th>Changed By</th>
                            <th>Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vendorStats.recentChanges.slice(0, 5).map((change, idx) => (
                            <tr key={idx}>
                              <td>{change.vendorName}</td>
                              <td>
                                <Badge bg={getStatusBadgeColor(change.previousStatus)}>
                                  {change.previousStatus}
                                </Badge>
                              </td>
                              <td>
                                <Badge bg={getStatusBadgeColor(change.status)}>
                                  {change.status}
                                </Badge>
                              </td>
                              <td>{change.changedBy || 'System'}</td>
                              <td>{new Date(change.timestamp).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </motion.div>
            )}

            {/* Message Alert */}
            {message.text && (
              <Alert 
                variant={message.type === "success" ? "success" : "danger"} 
                className="mb-3"
                dismissible
                onClose={() => setMessage({ type: "", text: "" })}
              >
                {message.text}
              </Alert>
            )}

            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-4"
            >
              {/* Customers Tab */}
              <Tab eventKey="customers" title={<span><FaUsers className="me-2" />Customers</span>}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="table-wrapper"
                >
                  <Table responsive bordered hover className="customers-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Orders</th>
                        <th>Total Spent</th>
                        <th>Avg. Order</th>
                        <th>Last Order</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCustomers.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center text-muted py-4">
                            {searchTerm ? "No customers match your search" : "No customers found"}
                          </td>
                        </tr>
                      ) : (
                        filteredCustomers.map((customer, idx) => (
                          <motion.tr
                            key={customer.email || idx}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                          >
                            <td>{idx + 1}</td>
                            <td className="fw-semibold">{customer.name || "N/A"}</td>
                            <td>{customer.email || "N/A"}</td>
                            <td>{customer.phone || "N/A"}</td>
                            <td>
                              <Badge bg="primary" pill>
                                {customer.orderCount || 0}
                              </Badge>
                            </td>
                            <td className="fw-bold text-success">
                              ₹{(customer.totalSpent || 0).toFixed(2)}
                            </td>
                            <td>
                              ₹{((customer.totalSpent || 0) / (customer.orderCount || 1)).toFixed(2)}
                            </td>
                            <td>
                              {customer.lastOrderDate 
                                ? new Date(customer.lastOrderDate).toLocaleDateString() 
                                : "N/A"}
                            </td>
                          </motion.tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                  
                  <div className="row mt-4">
                    <div className="col-md-3">
                      <div className="summary-card p-3 border rounded bg-light">
                        <h6 className="text-muted">Total Customers</h6>
                        <h3>{customers.length}</h3>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="summary-card p-3 border rounded bg-light">
                        <h6 className="text-muted">Total Revenue</h6>
                        <h3 className="text-success">
                          ₹{customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0).toFixed(2)}
                        </h3>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="summary-card p-3 border rounded bg-light">
                        <h6 className="text-muted">Total Orders</h6>
                        <h3>{customers.reduce((sum, c) => sum + (c.orderCount || 0), 0)}</h3>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="summary-card p-3 border rounded bg-light">
                        <h6 className="text-muted">Avg. Order Value</h6>
                        <h3>
                          ₹{(customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0) / 
                            (customers.reduce((sum, c) => sum + (c.orderCount || 0), 1) || 1)).toFixed(2)}
                        </h3>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Tab>

              {/* Vendors Tab with Plan Display */}
              <Tab eventKey="vendors" title={<span><FaStore className="me-2" />Vendors ({vendors.length})</span>}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="table-wrapper"
                >
                  <Table responsive bordered hover className="customers-table">
                    <thead>
                      <tr>
                        <th>
                          <Form.Check
                            type="checkbox"
                            checked={selectedVendors.length === filteredVendors.length && filteredVendors.length > 0}
                            onChange={selectAllVendors}
                          />
                        </th>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Company</th>
                        <th>Plan</th>
                        <th>Commission</th>
                        <th>Fee</th>
                        <th>Status</th>
                        <th>Joined</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVendors.length === 0 ? (
                        <tr>
                          <td colSpan="11" className="text-center text-muted py-4">
                            {searchTerm ? "No vendors match your search" : "No vendors found"}
                          </td>
                        </tr>
                      ) : (
                        filteredVendors.map((vendor, idx) => {
                          const planDetails = getPlanDetailsForVendor(vendor.plan);
                          const commission = vendor.commissionRate || planDetails.commissionRate;
                          const monthlyFee = planDetails.monthlyFee;
                          const statusOptions = getStatusChangeOptions(vendor);
                          const planColor = planDetails.color;
                          const planBgColor = planDetails.bgColor;
                          
                          return (
                            <motion.tr
                              key={vendor._id || idx}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: idx * 0.05 }}
                            >
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  checked={selectedVendors.includes(vendor._id)}
                                  onChange={() => toggleVendorSelection(vendor._id)}
                                />
                              </td>
                              <td>{idx + 1}</td>
                              <td className="fw-semibold">{vendor.name || "N/A"}</td>
                              <td>{vendor.email || "N/A"}</td>
                              <td>{vendor.company || "N/A"}</td>
                              <td>
                                <Badge 
                                  style={{ 
                                 
                              
                                    border: `1px solid ${planColor}`,
                                    fontSize: '13px', 
                                    padding: '5px 12px',
                                    fontWeight: '600'
                                  }}
                                >
                                  <FaTag className="me-1" /> {planDetails.name}
                                </Badge>
                              </td>
                              <td>
                                <span className="fw-bold" style={{ color: '#007bff', fontSize: '15px' }}>
                                  {commission}%
                                </span>
                              </td>
                              <td>
                                <span className="text-muted" style={{ fontSize: '14px' }}>
                                  <FaRupeeSign size={12} /> {monthlyFee.toLocaleString()}/mo
                                </span>
                              </td>
                              <td>
                                <Badge 
                                  bg={getStatusBadgeColor(vendor.status)}
                                  style={{ fontSize: '14px', padding: '6px 12px' }}
                                >
                                  {STATUS_OPTIONS.find(s => s.value === vendor.status)?.icon} {vendor.status || 'pending'}
                                </Badge>
                              </td>
                              <td>
                                {vendor.createdAt 
                                  ? new Date(vendor.createdAt).toLocaleDateString() 
                                  : "N/A"}
                              </td>
                              <td>
                                <div className="d-flex gap-1 flex-wrap">
                                  <Dropdown>
                                    <Dropdown.Toggle 
                                      variant={vendor.status === 'suspended' ? 'danger' : 'outline-warning'} 
                                      size="sm"
                                    >
                                      <FaExclamationTriangle /> Status
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                      {statusOptions.map(opt => (
                                        <Dropdown.Item 
                                          key={opt.value}
                                          onClick={() => handleStatusChange(vendor, opt.value)}
                                        >
                                          {opt.icon} Change to {opt.label}
                                        </Dropdown.Item>
                                      ))}
                                      <Dropdown.Divider />
                                      <Dropdown.Item 
                                        onClick={() => handleEditVendor(vendor)}
                                      >
                                        <FaEdit /> Edit Vendor
                                      </Dropdown.Item>
                                    </Dropdown.Menu>
                                  </Dropdown>
                                  
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    onClick={() => handleEditVendor(vendor)}
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => handleDeleteVendor(vendor._id)}
                                  >
                                    <FaTrash />
                                  </Button>
                                </div>
                              </td>
                            </motion.tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </motion.div>
              </Tab>
            </Tabs>
          </Container>
        </main>
      </div>

      {/* Status Change Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {newStatus === 'suspended' ? '⚠️ Suspend Vendor' : 
             newStatus === 'active' ? '✅ Activate Vendor' :
             `Change Status to ${newStatus}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVendor && (
            <>
              <p>
                <strong>Vendor:</strong> {selectedVendor.name} ({selectedVendor.email})
              </p>
              <p>
                <strong>Current Status:</strong>{' '}
                <Badge bg={getStatusBadgeColor(selectedVendor.status)}>
                  {selectedVendor.status}
                </Badge>
              </p>
              <p>
                <strong>New Status:</strong>{' '}
                <Badge bg={getStatusBadgeColor(newStatus)}>
                  {STATUS_OPTIONS.find(s => s.value === newStatus)?.label || newStatus}
                </Badge>
              </p>
              
              <Form.Group className="mt-3">
                <Form.Label>
                  {newStatus === 'suspended' ? 'Reason for Suspension' : 
                   'Reason for Status Change (Optional)'}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  placeholder={
                    newStatus === 'suspended' 
                      ? 'Please provide a reason for suspension...' 
                      : 'Optional: Add a note about this status change...'
                  }
                />
              </Form.Group>

              {newStatus === 'suspended' && (
                <Alert variant="danger" className="mt-3">
                  <FaExclamationTriangle className="me-2" />
                  <strong>Warning:</strong> Suspending this vendor will:
                  <ul className="mt-2 mb-0">
                    <li>Prevent them from selling products</li>
                    <li>Hide their store from the marketplace</li>
                    <li>Notify them via email</li>
                  </ul>
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button 
            variant={
              newStatus === 'suspended' ? 'danger' : 
              newStatus === 'active' ? 'success' : 'warning'
            }
            onClick={handleUpdateStatus}
            disabled={statusActionLoading}
          >
            {statusActionLoading ? <Spinner size="sm" className="me-2" /> : null}
            {newStatus === 'suspended' ? 'Confirm Suspension' : 
             newStatus === 'active' ? 'Activate Vendor' : 
             `Change to ${STATUS_OPTIONS.find(s => s.value === newStatus)?.label || newStatus}`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Action Modal */}
      <Modal show={showBulkActionModal} onHide={() => setShowBulkActionModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Bulk Action - {selectedVendors.length} Vendors</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Select Action</Form.Label>
            <Form.Select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
            >
              <option value="">Choose action...</option>
              <option value="active">✅ Activate All</option>
              <option value="suspended">⚠️ Suspend All</option>
              <option value="inactive">⏸️ Set Inactive</option>
              <option value="pending">🕐 Set Pending</option>
            </Form.Select>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Reason (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={bulkReason}
              onChange={(e) => setBulkReason(e.target.value)}
              placeholder="Reason for bulk action..."
            />
          </Form.Group>

          {bulkAction === 'suspended' && (
            <Alert variant="danger">
              <FaExclamationTriangle className="me-2" />
              You are about to suspend {selectedVendors.length} vendors. This action will hide their stores.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkActionModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger"
            onClick={handleBulkAction}
            disabled={!bulkAction || statusActionLoading}
          >
            {statusActionLoading ? <Spinner size="sm" className="me-2" /> : null}
            Confirm Bulk Action
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add/Edit Vendor Modal */}
      <Modal show={showVendorModal} onHide={() => setShowVendorModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingVendor ? "Edit Vendor" : "Add New Vendor"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={vendorForm.name}
                    onChange={handleVendorInput}
                    placeholder="Enter vendor name"
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={vendorForm.email}
                    onChange={handleVendorInput}
                    placeholder="Enter vendor email"
                    required
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>
                {editingVendor ? "Password (leave blank to keep current)" : "Password *"}
              </Form.Label>
              <div className="position-relative">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={vendorForm.password}
                  onChange={handleVendorInput}
                  placeholder={editingVendor ? "Enter new password" : "Enter password"}
                  required={!editingVendor}
                />
                <Button
                  variant="link"
                  className="position-absolute end-0 top-0"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ padding: '8px 12px' }}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </div>
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={vendorForm.phone}
                    onChange={handleVendorInput}
                    placeholder="Enter phone number"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Company</Form.Label>
                  <Form.Control
                    type="text"
                    name="company"
                    value={vendorForm.company}
                    onChange={handleVendorInput}
                    placeholder="Enter company name"
                  />
                </Form.Group>
              </div>
            </div>

            {/* Plan Selection */}
            <Form.Group className="mb-3">
              <Form.Label>Seller Pricing Plan <span className="text-danger">*</span></Form.Label>
              <div className="plan-selection-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                {PLAN_OPTIONS.map((plan) => {
                  const planDetails = getPlanDetailsForVendor(plan.value);
                  const isSelected = vendorForm.plan === plan.value;
                  
                  return (
                    <div 
                      key={plan.value}
                      className={`plan-option ${isSelected ? 'selected' : ''}`}
                      onClick={() => setVendorForm({ ...vendorForm, plan: plan.value })}
                      style={{
                        border: `2px solid ${isSelected ? planDetails.color : '#e0e0e0'}`,
                        borderRadius: '10px',
                        padding: '15px',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? planDetails.bgColor : 'white',
                        transition: 'all 0.3s ease',
                        boxShadow: isSelected ? `0 0 20px ${planDetails.color}30` : 'none',
                        position: 'relative'
                      }}
                    >
                      <div className="d-flex flex-column align-items-center text-center">
                        <div style={{ fontSize: '28px', color: planDetails.color }}>
                          <FaTag />
                        </div>
                        <div className="fw-bold mt-2" style={{ color: planDetails.color }}>
                          {planDetails.name}
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          {planDetails.commissionRate}% Commission
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: planDetails.color }}>
                          ₹{planDetails.monthlyFee.toLocaleString()}/mo
                        </div>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                          {planDetails.description}
                        </div>
                        <div className="mt-2" style={{ fontSize: '11px', color: '#888' }}>
                          {planDetails.features.slice(0, 3).map((feature, i) => (
                            <div key={i}>• {feature}</div>
                          ))}
                          {planDetails.features.length > 3 && (
                            <div style={{ color: '#aaa' }}>+{planDetails.features.length - 3} more</div>
                          )}
                        </div>
                        {isSelected && (
                          <div style={{ color: planDetails.color, marginTop: '6px' }}>
                            <Badge bg="success">✓ Selected</Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Form.Group>

            {/* Status Selection */}
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={vendorForm.status}
                onChange={handleVendorInput}
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVendorModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveVendor} disabled={modalLoading}>
            {modalLoading ? <Spinner size="sm" className="me-2" /> : null}
            {editingVendor ? "Update" : "Add"} Vendor
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Customer;